import { useId, useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

interface ConfirmDialogProps {
  open: boolean
  titleKey: string
  titleValues?: Record<string, string | number>
  bodyKey: string
  bodyValues?: Record<string, string | number>
  confirmKey: string
  cancelKey: string
  tone?: 'default' | 'danger'
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
  children?: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

/**
 * Shared accessible confirmation dialog for join/leave/cancel/join-request
 * decisions. It keeps copy in the catalogs, traps focus and restores it on
 * close so destructive actions are always confirmed explicitly.
 */
export function ConfirmDialog({
  open,
  titleKey,
  titleValues,
  bodyKey,
  bodyValues,
  confirmKey,
  cancelKey,
  tone = 'default',
  busy = false,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation()
  const titleId = useId()
  const bodyId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const onCancelRef = useRef(onCancel)

  useEffect(() => {
    onCancelRef.current = onCancel
  }, [onCancel])

  useEffect(() => {
    if (!open) return

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const timer = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)
      ;(first ?? cancelRef.current)?.focus()
    }, 0)

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancelRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (!focusable || focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      previouslyFocused?.focus()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-deep/50 p-0 min-[600px]:items-center min-[600px]:p-6">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className="max-h-[90dvh] w-full max-w-[460px] overflow-y-auto rounded-t-card bg-paper p-5 pb-[calc(1.25rem+var(--safe-bottom))] shadow-float min-[600px]:rounded-card"
      >
        <h2
          id={titleId}
          className="font-display text-xl font-extrabold text-deep"
        >
          {t(titleKey, titleValues)}
        </h2>
        <p
          id={bodyId}
          className="mt-2 text-sm leading-relaxed text-muted"
        >
          {t(bodyKey, bodyValues)}
        </p>

        {children ? <div className="mt-4">{children}</div> : null}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            className="btn-ghost px-3 text-sm"
            disabled={busy}
            onClick={onCancel}
          >
            {t(cancelKey)}
          </button>
          <button
            type="button"
            className={
              tone === 'danger'
                ? 'btn-primary min-h-11 bg-danger px-4 text-sm text-white'
                : 'btn-primary min-h-11 px-4 text-sm'
            }
            disabled={busy}
            onClick={onConfirm}
          >
            {t(confirmKey)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
